import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { cartApi, type Cart, type CartLine } from "@/features/cart/api";
import { useAuth } from "@/features/auth/AuthContext";
import { catalogApi } from "@/features/catalog/api";

/**
 * Cart store — server-backed for signed-in users, localStorage for guests.
 *
 * Flow:
 *  • Guest actions read/write localStorage; UI is hydrated with product data
 *    from the catalog API on demand.
 *  • On login, we merge the guest cart into the server cart (server dedupes
 *    and caps to stock). The merged server cart becomes the source of truth.
 */

type GuestLine = { product_id: string; quantity: number };

type CartContextValue = {
    cart: Cart;
    isLoading: boolean;
    getQuantity: (productId: string) => number;
    add: (productId: string, delta?: number) => Promise<void>;
    setQuantity: (productId: string, quantity: number) => Promise<void>;
    remove: (productId: string) => Promise<void>;
    clear: () => Promise<void>;
    saveForLater: (productId: string) => Promise<void>;
    restore: (productId: string) => Promise<void>;
    refresh: () => Promise<void>;
};

const EMPTY_CART: Cart = {
    items: [],
    saved_for_later: [],
    subtotal: 0,
    mrp_total: 0,
    total_savings: 0,
    total_items: 0,
};

const CartContext = createContext<CartContextValue | undefined>(undefined);
const GUEST_STORAGE_KEY = "tsgo-cart";
const MAX_QTY = 20;

function readGuest(): GuestLine[] {
    if (typeof window === "undefined") return [];
    try {
        const raw = window.localStorage.getItem(GUEST_STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw) as unknown;
        if (Array.isArray(parsed)) {
            return parsed
                .map((l) => l as { product_id?: string; productId?: string; quantity?: number })
                .map((l) => ({
                    product_id: (l.product_id ?? l.productId ?? "").toString(),
                    quantity: Number(l.quantity ?? 0),
                }))
                .filter((l) => l.product_id && l.quantity > 0);
        }
    } catch {
        /* ignore */
    }
    return [];
}

function writeGuest(items: GuestLine[]) {
    try {
        window.localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(items));
    } catch {
        /* ignore */
    }
}

function computeGuestTotals(lines: CartLine[]): Cart {
    const subtotal = lines.reduce((s, l) => s + l.price * l.quantity, 0);
    const mrp_total = lines.reduce((s, l) => s + l.mrp * l.quantity, 0);
    const total_items = lines.reduce((s, l) => s + l.quantity, 0);
    return {
        items: lines,
        saved_for_later: [],
        subtotal,
        mrp_total,
        total_savings: Math.max(0, mrp_total - subtotal),
        total_items,
    };
}

export function CartProvider({ children }: { children: React.ReactNode }) {
    const { status, isAuthenticated } = useAuth();
    const [cart, setCart] = useState<Cart>(EMPTY_CART);
    const [isLoading, setIsLoading] = useState(false);
    const mergedRef = useRef(false);

    const hydrateGuest = useCallback(async () => {
        const lines = readGuest();
        if (!lines.length) {
            setCart(EMPTY_CART);
            return;
        }
        setIsLoading(true);
        try {
            const products = await Promise.all(
                lines.map((l) => catalogApi.getProduct(l.product_id).catch(() => null)),
            );
            const enriched: CartLine[] = [];
            for (let i = 0; i < products.length; i += 1) {
                const p = products[i];
                if (!p) continue;
                enriched.push({
                    product_id: p.id,
                    slug: p.slug,
                    name: p.name,
                    brand: p.brand,
                    price: p.price,
                    mrp: p.mrp,
                    weight: p.weight,
                    tone: p.tone as string[],
                    glyph: p.glyph,
                    quantity: Math.min(lines[i].quantity, p.stock_count, MAX_QTY),
                    stock_count: p.stock_count,
                    in_stock: p.in_stock,
                    images: p.images as string[],
                    show_picture: p.show_picture,
                });
            }
            setCart(computeGuestTotals(enriched));
        } finally {
            setIsLoading(false);
        }
    }, []);

    const refreshServer = useCallback(async () => {
        setIsLoading(true);
        try {
            const data = await cartApi.get();
            setCart(data);
        } catch {
            setCart(EMPTY_CART);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Sync when auth status changes
    useEffect(() => {
        if (status === "loading") return;
        if (isAuthenticated) {
            const merge = async () => {
                if (mergedRef.current) {
                    await refreshServer();
                    return;
                }
                const guest = readGuest();
                if (guest.length) {
                    try {
                        const merged = await cartApi.merge(guest);
                        setCart(merged);
                    } catch {
                        await refreshServer();
                    } finally {
                        writeGuest([]);
                    }
                } else {
                    await refreshServer();
                }
                mergedRef.current = true;
            };
            merge();
        } else {
            mergedRef.current = false;
            hydrateGuest();
        }
    }, [status, isAuthenticated, hydrateGuest, refreshServer]);

    const persistGuest = useCallback((next: CartLine[]) => {
        writeGuest(next.map((l) => ({ product_id: l.product_id, quantity: l.quantity })));
    }, []);

    const optimisticGuestAdd = useCallback(
        async (productId: string, delta: number) => {
            const existing = cart.items.find((l) => l.product_id === productId);
            if (existing) {
                const q = Math.max(0, Math.min(MAX_QTY, existing.stock_count, existing.quantity + delta));
                const next = q === 0
                    ? cart.items.filter((l) => l.product_id !== productId)
                    : cart.items.map((l) =>
                          l.product_id === productId ? { ...l, quantity: q } : l,
                      );
                persistGuest(next);
                setCart(computeGuestTotals(next));
                return;
            }
            if (delta <= 0) return;
            const p = await catalogApi.getProduct(productId);
            const q = Math.min(delta, p.stock_count, MAX_QTY);
            const next: CartLine[] = [
                ...cart.items,
                {
                    product_id: p.id,
                    slug: p.slug,
                    name: p.name,
                    brand: p.brand,
                    price: p.price,
                    mrp: p.mrp,
                    weight: p.weight,
                    tone: p.tone as string[],
                    glyph: p.glyph,
                    quantity: q,
                    stock_count: p.stock_count,
                    in_stock: p.in_stock,
                    images: p.images as string[],
                    show_picture: p.show_picture,
                },
            ];
            persistGuest(next);
            setCart(computeGuestTotals(next));
        },
        [cart, persistGuest],
    );

    const add = useCallback(
        async (productId: string, delta = 1) => {
            if (isAuthenticated) {
                const updated = await cartApi.add(productId, delta);
                setCart(updated);
            } else {
                await optimisticGuestAdd(productId, delta);
            }
        },
        [isAuthenticated, optimisticGuestAdd],
    );

    const setQuantity = useCallback(
        async (productId: string, quantity: number) => {
            if (isAuthenticated) {
                const updated = await cartApi.setQuantity(productId, Math.max(0, Math.min(MAX_QTY, quantity)));
                setCart(updated);
            } else {
                const existing = cart.items.find((l) => l.product_id === productId);
                if (!existing) {
                    if (quantity > 0) await add(productId, quantity);
                    return;
                }
                const delta = quantity - existing.quantity;
                await optimisticGuestAdd(productId, delta);
            }
        },
        [isAuthenticated, cart, add, optimisticGuestAdd],
    );

    const remove = useCallback(
        async (productId: string) => {
            if (isAuthenticated) {
                const updated = await cartApi.remove(productId);
                setCart(updated);
            } else {
                const next = cart.items.filter((l) => l.product_id !== productId);
                persistGuest(next);
                setCart(computeGuestTotals(next));
            }
        },
        [isAuthenticated, cart, persistGuest],
    );

    const clear = useCallback(async () => {
        if (isAuthenticated) {
            const updated = await cartApi.clear();
            setCart(updated);
        } else {
            persistGuest([]);
            setCart(EMPTY_CART);
        }
    }, [isAuthenticated, persistGuest]);

    const saveForLater = useCallback(
        async (productId: string) => {
            if (!isAuthenticated) return; // saved-for-later requires auth
            const updated = await cartApi.saveForLater(productId);
            setCart(updated);
        },
        [isAuthenticated],
    );

    const restore = useCallback(
        async (productId: string) => {
            if (!isAuthenticated) return;
            const updated = await cartApi.restore(productId);
            setCart(updated);
        },
        [isAuthenticated],
    );

    const refresh = useCallback(async () => {
        if (isAuthenticated) await refreshServer();
        else await hydrateGuest();
    }, [isAuthenticated, refreshServer, hydrateGuest]);

    const getQuantity = useCallback(
        (productId: string) =>
            cart.items.find((l) => l.product_id === productId)?.quantity ?? 0,
        [cart],
    );

    const value = useMemo<CartContextValue>(
        () => ({
            cart,
            isLoading,
            getQuantity,
            add,
            setQuantity,
            remove,
            clear,
            saveForLater,
            restore,
            refresh,
        }),
        [cart, isLoading, getQuantity, add, setQuantity, remove, clear, saveForLater, restore, refresh],
    );

    return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within <CartProvider>");
    return ctx;
}

// Backwards-compat: expose totalCount for existing Navbar usage.
export function useCartTotalCount(): number {
    return useCart().cart.total_items;
}
